import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
    Form,
    Input,
    Select,
    DatePicker,
    Checkbox,
    Row,
    Space,
    message,
    Tooltip
} from 'antd';
import axios from "@common/axios";
import SyllabusTopicConceptSelect from "@common/SyllabusTopicConceptSelect";
import {UserContext} from "@context/UserContext";
import {BrandContext} from "@context/BrandContext";
import ClassSelect from "@common/ClassSelect";
import PopConfirm from "@common/PopConfirm";
import {ContentContext} from "@context/ContentContext";
import PrintTestColumnsDialog from "../PrintTestColumnsDialog";
import CopyTestsDialog from "../CopyTestsDialog";
import TableFilterControls from "@common/TableFilterControls";
import TableAntD from "@common/TableAntD";
import Button, {BUTTON_TYPE} from "@common/Button";
import CreateTestDialog from "../CreateTestDialog";
import ArchiveAllDialog from "../ArchiveAllDialog";
import ShareTestsDialog from "../ShareTestsDialog";
import PreviewTestDialog from "../PreviewTestDialog";
import AssignTestDialog from "../AssignTestDialog";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import {useFormFilterBadge} from "@hooks/useFormFilterBadge";
import {useScreenReaderAnnouncement} from "@hooks/useScreenReaderAnnouncment";
import TestWizard from "./TestWizard";
import DynamicContent from "../../../nav/DynamicContent";
import {teacherCreateTest} from "./axiosRequests/axiosRequests";
import ButtonsGroup from "@common/ButtonsGroup";

const { RangePicker } = DatePicker;

const TeacherTests = (props) => {
    const {testGroupCode, handleReactEditTest, handleCreateTestByWizard} = props,
        {user} = useContext(UserContext),
        {setContentLoading} = useContext(ContentContext),
        brandConfig = useContext(BrandContext),
        [testSearchForm] = Form.useForm(),
        classId = Form.useWatch('classId', testSearchForm),
        [testOwnerSearchType, setTestOwnerSearchType] = useState('TEACHER'),
        [searchType, setSearchType] = useState('mostRecent'),
        [archived, setArchived] = useState(false),
        [exam, setExam] = useState(null),
        [archiveBeforeDate, setArchiveBeforeDate] = useState(null),
        [error, setError] = useState(null),
        [selectedTest, setSelectedTest] = useState(),
        [selectedTests, setSelectedTests] = useState(() => new Map()),
        [isDisableEdit, setDisableEdit] = useState(true),
        [isDisableAssign, setDisableAssign] = useState(true),
        [isDisableArchive, setDisableArchive] = useState(true),
        [isDisableShare, setDisableShare] = useState(true),
        [isDisableCopy, setDisableCopy] = useState(true),
        [isCreateTestDialogOpen, setCreateTestDialogOpen] = useState(false),
        [isPrintTestDialogOpen, setPrintTestDialogOpen] = useState(false),
        [isArchiveAllDialogOpen, setArchiveAllDialogOpen] = useState(false),
        [isShareDialogOpen, setShareDialogOpen] = useState(false),
        [isPreviewTestDialogOpen, setPreviewTestDialogOpen] = useState(false),
        [testId, setTestId] = useState(),
        [isWizardTestOpen, setWizardTestOpen] = useState(false),
        [isCreateTestOpen, setCreateTestOpen] = useState(false),
        [createTestParams, setCreateTestParams] = useState(false),
        [isEditTestOpen, setEditTestOpen] = useState(false),
        [viewTestId, setViewTestId] = useState(null),
        [archiveAllForm] = Form.useForm(),
        [isCopyDialogOpen, setCopyDialogOpen] = useState(false),
        [isAssignDialogOpen, setAssignDialogOpen] = useState(false),
        [currentPage, setCurrentPage] = useState(1),
        [tests, setTests] = useState([]),
        [reload, setReload] = useState(),
        [hasExams, setHasExams] = useState(brandConfig.hasExams),
        {activeFilterCount, onLoadComplete, handleValuesChange: handleBadgeValuesChange} = useFormFilterBadge(testSearchForm),
        { setSrAnnouncement, LiveRegion, srAnnouncement } = useScreenReaderAnnouncement();

    globalReactFunctions.refreshTeacherTests = () => {
        setReload(new Date().getTime());
        clearSelectedTests();
        setCreateTestOpen(false);
        setEditTestOpen(false);
        setWizardTestOpen(false);
        setTestId(undefined);
    };

    const refreshTeacherTests = () => {
        setReload(new Date().getTime());
        clearSelectedTests();
    }

    const addSelectedTest = (key, value) => {
        if (!key) {
            return;
        }

        setSelectedTests(prevMap => {
            const newMap = new Map(prevMap);
            newMap.set(key, value);
            return newMap;
        });
    };

    const addAllSelectedTests = (tests) => {
        setSelectedTests(prevMap => {
            const newMap = new Map(prevMap);
            tests.forEach(test => {
                newMap.set(test.testId, test);
            });
            return newMap;
        });
    };

    const removeSelectedTest = (key) => {
        if (!key) {
            return;
        }

        setSelectedTests(prevMap => {
            const newMap = new Map(prevMap);
            newMap.delete(key);
            return newMap;
        });
    };

    const clearSelectedTests = () => {
        setSelectedTests(new Map());
    };

    const closeCreateTestDialog = () => {
        setCreateTestDialogOpen(false);
    }

    const handleEditTest = (testId) => {
        setCreateTestOpen(false);
        setWizardTestOpen(false);
        if (testId) {
            if (brandConfig.useReactTestEditor) {
                handleReactEditTest(testId);
            } else {
                setEditTestOpen(true);
                setTestId(testId);
            }
        } else {
            setEditTestOpen(false);
            setTestId(undefined);
        }
    }

    useEffect(() => {
        let disableEdit = true;
        let disableAssign = false;
        let disableArchive = true;
        let disableShare = true;
        let disableCopy = true;
        for (const test of selectedTests.values()) {
            const owned = test.teacherId === user.userId;
            const ownSchool = test.schoolId === user.schoolId;

            if (!test.autoMark || test.total == 0) {
                disableAssign = true;
            }
            if (test.editable && !test.started && owned) {
                disableEdit = false;
            }
            if (owned || ownSchool) {
                disableShare = false;
                disableCopy = false;
            }
            if (owned) {
                disableArchive = false;
            }
        }

        if (selectedTests.size == 1) {
            setSelectedTest(selectedTests.values().next().value);
        } else {
            setSelectedTest(undefined);
        }

        setDisableAssign(disableAssign);
        setDisableEdit(disableEdit);
        setDisableArchive(disableArchive);
        setDisableShare(disableShare);
        setDisableCopy(disableCopy);
    }, [selectedTests]);

    useEffect(() => {
        if (globalReactFunctions !== undefined && globalReactFunctions.createTestFromDashboard) {
            globalReactFunctions.createTestFromDashboard = undefined;
            openCreateTestDialog();
        }

        if (globalReactFunctions !== undefined && globalReactFunctions.createTestAssignmentFromDashboard) {
            const testId = globalReactFunctions.testId;
            globalReactFunctions.createTestAssignmentFromDashboard = undefined;
            globalReactFunctions.testId = undefined;
            openAssignTestsDialog([testId]);
        }
    }, []);

    useEffect(() => {
        if (!hasExams && ['TEST', 'DEMO'].includes(user.schoolGroupCode)) {
            axios.get('/web/ui/content/syllabusGroupHasQuestions', {params: {syllabusGroupId: user.syllabusGroupId, syllabusType: 'EXAM'}})
                .then(response => {
                    setHasExams(response.data);
                });
        }
    }, []);

    const columns = [
        {
            title: <Checkbox
                aria-label={`select all ${brandConfig.testsName}`}
                disabled={tests.length === 0}
                checked={tests.length > 0 && selectedTests.size === tests.length}
                onChange={(e) => onSelectAllRows(e.target.checked)}
            ><span className="ch-accessibility-label">select all tests</span></Checkbox>,
            dataIndex: 'checkbox',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    aria-label={`select ${brandConfig.testName} ${record.testName}`}
                    checked={selectedTests.has(record.testId)}
                    onChange={e => onSelectRow(e.target.checked, record)}
                ><span className="ch-accessibility-label">select test</span></Checkbox>
            )
        },
        {
            title: brandConfig.testName + ' name',
            dataIndex: 'name',
            sorter: false,
            render: (_, record) => <Tooltip trigger={['hover', 'focus']} title={(record.started ? 'Preview' : 'Edit') + ' ' + brandConfig.testName.toLowerCase()}><a className={'ch-link'} aria-label={(record.started ? 'preview' : 'edit') + ' ' + (record.name ? record.name : brandConfig.testName)} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') { handleViewTest(record) } }} onClick={() => handleViewTest(record)}>{record.name ? record.name : <span className="ch-empty-name">{'[' + brandConfig.testName + ' name]'}</span>}</a>{record.description && <span className="ch-text-note"><br />{record.description}</span>}</Tooltip>
        },
        {
            title: <span style={{whitespace: 'nowrap'}}>In Use</span>,
            dataIndex: 'started',
            align: 'center',
            render: (_, record) => record.archived ? 'Archived' : record.started ? 'Yes' : 'No'
        },
        {
            title: 'Questions',
            dataIndex: 'total',
            sorter: false,
            render: (_, record) => record.total
        },
        {
            title: 'Type',
            dataIndex: 'testPurpose',
            align: 'center',
            render: (_, record) => record.testPurposeDescription + (record.exam ? record.autoMark ? ' online' : ' PDF' : ''),
        },
        {
            title: 'Created',
            dataIndex: 'createDate',
            sorter: false,
            render: (value, record) => {
                if (record.createDate === undefined) {
                    return '';
                }

                const dateFormatted = user.dateTimeMinuteFormat.format(record.createDate);
                return <span>{dateFormatted}<br />{!record.diagnosticTest ? record.teacherFirstName + ' ' + record.teacherLastName : ''}</span>;
            }
        },
        {
            title: 'Assigned',
            dataIndex: 'assignedDate',
            sorter: false,
            render: (value, record) => {
                if (record.assignedDate === undefined) {
                    return '';
                }

                return user.dateTimeMinuteFormat.format(record.assignedDate);
            }
        }
    ];

    const onSelectRow = (checked, test) => {
        if (checked) {
            addSelectedTest(test.testId, test);
        } else {
            removeSelectedTest(test.testId);
        }
    };

    const onSelectAllRows = (checked) => {
        if (checked) {
            addAllSelectedTests(tests);
        } else {
            clearSelectedTests();
        }
    };

    const handleSearch = () => {
        handleBadgeValuesChange();
        setReload(new Date().getTime());
    };

    const handleValuesChange = (changedValues, allValues) => {
        const keys = Object.keys(changedValues);
        const hasOtherKeys = keys.some(key => key !== 'testName' && key !== 'dateRange');

        if (hasOtherKeys) {
            handleBadgeValuesChange();
        }

        if ('syllabusId' in changedValues || 'syllabusGroupId' in changedValues) {
            handleSearch();
        }
    }

    const handleTestOwnerSearchType = (value) => {
        setTestOwnerSearchType(value);
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
    };

    // FIX: Wrapped in useCallback so the function reference only changes when
    // its true dependencies change, preventing TableAntD from re-fetching on
    // every render due to a new function reference being passed each time.
    const fetchData = useCallback((pagingParams, handleTableData, handlePagingData, abortController) => {
        const formValues = testSearchForm.getFieldsValue();
        const testName = formValues.testName || undefined;
        const syllabusId = formValues.syllabusId || undefined;
        const [startDate, endDate] = formValues.dateRange || [];
        const clazzId = formValues.classId;

        const payload = {
            pageNumber: pagingParams.pageNumber,
            pageSize: pagingParams.pageSize,
            testOwnerSearchType: testOwnerSearchType,
            testGroupCode: testGroupCode,
            testName: testName,
            classId: clazzId,
            archived: archived,
            syllabusId: syllabusId,
            exam: exam,
            startDate: startDate,
            endDate: endDate
        };

        axios.post('/web/ui/test/teacher/search', payload, {
            headers: {
                'Content-Type': 'application/json'
            },
            signal: abortController.signal
        }).then(response => {
            const totalResults = response.data.totalResults ?? 0;
            const testsName = brandConfig.testsName.toLowerCase();

            handleTableData(response.data.currPageContents);
            setTests(response.data.currPageContents);
            clearSelectedTests();
            handlePagingData({
                pageSize: pagingParams.pageSize,
                totalResults: response.data.totalResults ?? 0,
            });

            const resultMessage = totalResults === 1 ? `1 ${brandConfig.testName.toLowerCase()}` : `${totalResults} ${testsName}`;
            setSrAnnouncement(`Table updated. ${resultMessage} found.`);
        })
            .catch(error => {
                console.error('Error loading data:', error);

                setError(error);
                handleTableData([]);
                setTests([]);
                handlePagingData({
                    pageSize: pagingParams.pageSize,
                    totalResults: 0
                });

                setSrAnnouncement(`Error loading ${brandConfig.testsName.toLowerCase()}. Please try again.`);
            });
    }, [testOwnerSearchType, testGroupCode, archived, exam, reload, classId]);
    const pagingData = useMemo(() => ({
        fetchFunction: fetchData,
        params: { pageSize: 10, currentPage: currentPage }
    }), [fetchData, currentPage]);

    function openCreateTestDialog() {
        setCreateTestDialogOpen(true);
    }

    const handleViewTest = (test) => {
        if (test == undefined) {
            message.error('Please select a test to view.');
            return;
        }
        if (test.started || test.adaptive) {
            setPreviewTestDialogOpen(true);
            setViewTestId(test.testId);
        } else {
            handleEditTest(test.testId);
        }
    }

    const handleViewButton = () => {
        let testId = undefined;
        if (selectedTests && selectedTests.size === 1) {
            testId = selectedTests.keys().next().value;
        }

        if (testId == undefined) {
            message.error('Please select a test to view.');
        }

        if (isDisableEdit) {
            setViewTestId(testId);
            setPreviewTestDialogOpen(true);
        } else {
            handleEditTest(testId);
        }
    }

    const handleAssign = () => {
        if (selectedTests && selectedTests.size === 1) {
            setAssignDialogOpen(true);
        } else {
            message.error('Please select a single test to assign.');
        }
    }

    const handleShare = () => {
        if (selectedTests.size > 0) {
            setShareDialogOpen(true);
        } else {
            message.error('Please select a single test to share.');
        }
    }

    const handleCopy = () => {
        if (selectedTest != undefined) {
            setCopyDialogOpen(true);
        } else {
            message.error('Please select a single test to copy.');
        }
    }

    const archiveTests = (pathwayIds) => {
        const params = new URLSearchParams();
        params.append('testIds', pathwayIds);

        axios.post('/controlCentre/archiveTest.action', params.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then(response => {
            displayToastMessage(brandConfig.testsName + ' successfully archived', brandConfig.testsName + ' archived successfully', 'success');
            setContentLoading(true);
            handleSearch();
        }).catch(error => {
            console.error(error);
            setError(error);
        }).finally(() => {
            setContentLoading(false);
        });
    }

    const unarchiveTests = (pathwayIds) => {
        const params = new URLSearchParams();
        params.append('testIds', pathwayIds);

        axios.post('/controlCentre/unarchiveTests.action', params.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).then(response => {
            displayToastMessage(brandConfig.testsName + ' successfully unarchived', brandConfig.testsName + ' unarchived successfully', 'success');
            setContentLoading(true);
            handleSearch();
        }).catch(error => {
            console.error(error);
            setError(error);
        }).finally(() => {
            setContentLoading(false);
        });
    }

    const handleArchiveTests = () => {
        if (selectedTests.size > 0) {
            archiveTests(Array.from(selectedTests.keys()));
        } else {
            message.error('Please select tests to archive.');
        }
    }

    const handleUnarchiveTests = () => {
        if (selectedTests.size > 0) {
            unarchiveTests(Array.from(selectedTests.keys()));
        } else {
            message.error('Please select tests to unarchive.');
        }
    }

    const handleArchiveAll = () => {
        return new Promise((resolve, reject) => {
            if (archiveBeforeDate === null) {
                archiveAllForm.validateFields();
                reject('Archive date is invalid');
                return;
            }
            axios.post('/web/ui/test/teacher/archiveTestsBeforeDate', null, {params: {archiveDate: archiveBeforeDate}})
                .then(response => {
                    if (response.data.success) {
                        refreshTeacherTests();
                    } else {
                        reject('Error archiving all tests before date');
                    }
                    resolve();
                }).catch(error => {
                console.error('Error archiving all tests before date', error);
                reject('Error archiving all tests before date', error);
            }).finally(() => {
                setArchiveAllDialogOpen(false);
            });
        });
    }

    const handlePrintTest = () => {
        if (selectedTest != undefined) {
            if (selectedTest.exam) {
                printTestWithColumn(1, selectedTest.testId);
            } else {
                setPrintTestDialogOpen(true);
            }
        } else {
            message.error('Please select a single test to print.');
        }
    };

    const handlePrintTestColumn = (numberColumns = 1) => {
        if (selectedTest != undefined) {
            setPrintTestDialogOpen(false);
            printTestWithColumn(numberColumns, selectedTest.testId);
        } else {
            message.error('Please select a single test to print.');
        }
        return Promise.resolve();
    };

    const handlePrintTestMarkingKey = () => {
        if (selectedTests.size === 1) {
            printTestMarkingKey(selectedTests.keys().next().value);
        } else {
            message.error('Please select a single test to print.');
        }
    };

    const handleSetArchiveDate = (date) => {
        setArchiveBeforeDate(date);
    }

    const handleCreateTest = (testParams) => {
        if (brandConfig.useReactTestEditor) {
            teacherCreateTest(testParams)
                .then((testBean) => {
                    if (testBean?.testId) {
                        handleReactEditTest(testBean.testId);
                    }
                })
                .catch(err => console.error("Navigation failed", err));
        } else {
            setWizardTestOpen(false);
            setEditTestOpen(false);
            setCreateTestDialogOpen(false);
            setCreateTestParams(testParams);
            setTestId(undefined);
            setCreateTestOpen(true);
        }
    }

    const handleWizardCreateTest = () => {
        if (brandConfig.useReactTestEditor) {
            handleCreateTestByWizard()
        } else {
            setCreateTestOpen(false);
            setEditTestOpen(false);
            setCreateTestDialogOpen(false);
            setTestId(undefined);
            setWizardTestOpen(true);
        }
    }

    const renderButtons = () => {
        return (
            <Row justify="start" className="margin-t--m margin-b--m">
                <nav aria-label={`${brandConfig.testName} actions`}>
                    <ButtonsGroup className={'flexWrap'}>
                        {testGroupCode == undefined && <>
                            <Button onClick={openCreateTestDialog} aria-label={`Create ${brandConfig.testName}`}>Create</Button>
                        </>
                        }
                        <Button type={BUTTON_TYPE.OUTLINE} aria-label={`View ${brandConfig.testName}`} onClick={handleViewButton}
                                disabled={selectedTests.size !== 1}>View</Button>
                        <Button type={BUTTON_TYPE.OUTLINE}
                                onClick={handleAssign}
                                aria-label={`Assign ${brandConfig.testName}`}
                                disabled={selectedTests.size !== 1 || isDisableAssign}>Assign</Button>
                        {testGroupCode == undefined && <>
                            <Button type={BUTTON_TYPE.OUTLINE} aria-label={`Share ${brandConfig.testName}`} onClick={handleShare}
                                    disabled={isDisableShare}>Share</Button>
                            <Button type={BUTTON_TYPE.OUTLINE} aria-label={`Copy ${brandConfig.testName}`} onClick={handleCopy}
                                    disabled={selectedTests.size !== 1 || isDisableCopy}>Copy</Button>
                            {archived ?
                                <PopConfirm title={<span>Unarchive {brandConfig.testName}?</span>}
                                            okText='Unarchive'
                                            onConfirm={handleUnarchiveTests}>
                                    <Button type={BUTTON_TYPE.OUTLINE} aria-label={`Unarchive ${brandConfig.testName}`} disabled={isDisableArchive}>Unarchive</Button>
                                </PopConfirm> :
                                <PopConfirm
                                    title={<span>Archive {brandConfig.testName}?</span>}
                                    okText='Archive'
                                    onConfirm={handleArchiveTests}
                                >
                                    <Button type={BUTTON_TYPE.OUTLINE} aria-label={`Archive ${brandConfig.testName}`} disabled={isDisableArchive}>Archive</Button>
                                </PopConfirm>
                            }
                        </>
                        }
                        <Button type={BUTTON_TYPE.OUTLINE} aria-label={`Print ${brandConfig.testName}`} onClick={handlePrintTest}
                                disabled={selectedTests.size !== 1}>Print </Button>
                        <Button type={BUTTON_TYPE.OUTLINE} aria-label={`${brandConfig.testName} Answer key`} onClick={handlePrintTestMarkingKey}
                                disabled={selectedTests.size !== 1}>Answer Key</Button>
                        {testGroupCode == undefined && !archived &&
                            <Button type={BUTTON_TYPE.OUTLINE} aria-label={`Archive all ${brandConfig.testsName}`} onClick={() => { setArchiveAllDialogOpen(true) }}>Archive All</Button>
                        }
                    </ButtonsGroup>
                </nav>
            </Row>
        )
    }

    return (
        <>
            <LiveRegion message={srAnnouncement} />
            {!isWizardTestOpen && !isEditTestOpen && !isCreateTestOpen &&
                <div>
                    {testGroupCode == undefined &&
                        <TableFilterControls
                            filterCount={activeFilterCount}
                            showFilterButton={true}
                            primaryFilterItem={
                                <Form.Item label={brandConfig.testsName}>
                                    <Select
                                        aria-label={`${brandConfig.testName} owner`}
                                        value={testOwnerSearchType}
                                        onChange={handleTestOwnerSearchType}
                                        style={{ width: 180 }}
                                        getPopupContainer={trigger => trigger.parentElement}
                                    >
                                        <Select.Option value="TEACHER">
                                            My {brandConfig.testsName.toLowerCase()}
                                        </Select.Option>
                                        <Select.Option value="CLASSES">
                                            Classes {brandConfig.testsName.toLowerCase()}
                                        </Select.Option>
                                        <Select.Option value="SCHOOL">
                                            School {brandConfig.testsName.toLowerCase()}
                                        </Select.Option>
                                    </Select>
                                </Form.Item>
                            }
                            filterContent={
                                <TableFilterControlsForm
                                    form={testSearchForm}
                                    onFinish={handleSearch}
                                    onValuesChange={handleValuesChange}
                                    initialValues={{ nameSearch: '' }}
                                    wrapperCol={{ flex: 'auto' }}
                                    useApplyButton={true}
                                >
                                    <TableFilterControlsSection>
                                        <Form.Item label={`${brandConfig.testName} name`} name="testName">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="Class" name="classId">
                                            <ClassSelect
                                                classes={user.classes}
                                                width={'100%'}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Created date" name="dateRange">
                                            <RangePicker format={user.getDateFormatString()} style={{width: '100%'}} getPopupContainer={trigger => trigger.parentElement} />
                                        </Form.Item>
                                        <Form.Item
                                            name="contentCategory"
                                            label="Content category"
                                        >
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                showSearch={false}
                                                getPopupContainer={trigger => trigger.parentElement}
                                                onChange={(selectedValues) => {
                                                    setExam(selectedValues.includes('filter-by-exam') ? true : null);
                                                    setArchived(selectedValues.includes('archived-only'));
                                                }}
                                            >
                                                <Select.Option value="filter-by-exam">Exam Only</Select.Option>
                                                <Select.Option value="archived-only">Archived Only</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </TableFilterControlsSection>
                                    <TableFilterControlsSection showDivider={false}>
                                        <SyllabusTopicConceptSelect
                                            form={testSearchForm}
                                            selectLevel="syllabus"
                                            defaultLevel="syllabusGroup"
                                            width={'100%'}
                                            onLoadComplete={onLoadComplete}
                                        />
                                    </TableFilterControlsSection>
                                </TableFilterControlsForm>
                            } />
                    }
                    {renderButtons()}
                    <TableAntD
                        dynamicWidth={true}
                        pagingDependencies={[reload, searchType, archived, classId, exam, testOwnerSearchType, testGroupCode]}
                        pagingData={pagingData}
                        columnData={columns}
                        rowKey="testId"
                        onChange={handleTableChange}
                        locale={{
                            emptyText: (
                                <span className="ch-warning-text">No {brandConfig.testsName.toLowerCase()} found</span>
                            )
                        }}
                    />
                    {renderButtons()}
                </div>
            }
            {(isWizardTestOpen && !brandConfig.useReactTestEditor) &&
                <TestWizard onClose={() => setWizardTestOpen(false)} onTestGenerated={handleEditTest} />
            }
            {(isCreateTestOpen && !brandConfig.useReactTestEditor) &&
                <DynamicContent className="js-editTestContainer" url={'/controlCentre/createTest.action'} params={createTestParams} />
            }
            {(isEditTestOpen && !brandConfig.useReactTestEditor) && testId &&
                <DynamicContent className="js-editTestContainer" url={'/controlCentre/editTest.action'} params={{testId: testId}} />
            }
            <AssignTestDialog open={isAssignDialogOpen} onClose={() => setAssignDialogOpen(false)} setReload={handleSearch} selectedTests={[selectedTest]} />
            {testGroupCode === undefined && <ShareTestsDialog
                open={isShareDialogOpen}
                handleDialogOpen={(val) => { setShareDialogOpen(val) }}
                testIds={Array.from(selectedTests.keys())}
                testName={brandConfig.testName}
                testsName={brandConfig.testsName}
            />}
            {testGroupCode === undefined && <CopyTestsDialog
                sharedTest={false}
                onSearch={handleSearch}
                open={isCopyDialogOpen}
                onClose={() => {
                    setCopyDialogOpen(false);
                }}
                selectedTest={selectedTest}
            />}
            <PrintTestColumnsDialog
                open={isPrintTestDialogOpen}
                onClose={
                    () => {
                        setPrintTestDialogOpen(false);
                    }
                }
                onPrintTest={handlePrintTestColumn}
                selectedSingleTest={selectedTest}
            />
            {testGroupCode === undefined && <CreateTestDialog
                open={isCreateTestDialogOpen}
                onClose={closeCreateTestDialog}
                onWizardCreateTest={handleWizardCreateTest}
                onCreateTest={handleCreateTest}
                hasExams={hasExams}
            />}
            <PreviewTestDialog
                open={isPreviewTestDialogOpen}
                onClose={() => { setPreviewTestDialogOpen(false) }}
                testId={viewTestId}
            />
            {testGroupCode === undefined && <ArchiveAllDialog archiveBeforeDate={archiveBeforeDate}
                                                              handleSetArchiveDate={handleSetArchiveDate}
                                                              archiveAllForm={archiveAllForm}
                                                              onArchiveAll={handleArchiveAll}
                                                              forAssessmentsShortcut={true}
                                                              onClose={() => { setArchiveAllDialogOpen(false) }}
                                                              user={user} open={isArchiveAllDialogOpen}
            />}
        </>
    );
};

export default TeacherTests;